<form [formGroup]="form" (ngSubmit)="submit()">
    <div class="modal-header">
        <h2 class="modal-title fs-5">
            {{ titleKey | translate: 'online' : 'export' }}
        </h2>
        <button
            type="button"
            class="btn-close"
            [attr.aria-label]="'reporting_CANCEL' | translate"
            [disabled]="isSaving()"
            (click)="dismiss()"></button>
    </div>

    <div class="modal-body">
        <div class="row g-4">
            <div class="col-12 col-lg-6">
                <app-input-text-group
                    formControlName="name"
                    [label]="
                        'sftpconnection_create_modal_name_label' | translate: 'online' : 'export'
                    "
                    [placeholder]="
                        'sftpconnection_create_modal_name_placeholder'
                            | translate: 'online' : 'export'
                    "
                    [information]="
                        'sftpconnection_modal_name_hint' | translate: 'online' : 'export'
                    "
                    [maxLength]="nameMaxLength"
                    [autoFocus]="true" />

                <div formGroupName="credentials">
                    <div class="row g-3">
                        <div class="col-8">
                            <app-input-text-group
                                formControlName="host"
                                [label]="
                                    'sftpconnection_modal_host_label'
                                        | translate: 'online' : 'export'
                                "
                                [placeholder]="
                                    'sftpconnection_modal_host_placeholder'
                                        | translate: 'online' : 'export'
                                "
                                [maxLength]="hostMaxLength" />
                        </div>
                        <div class="col-4">
                            <app-input-text-group
                                formControlName="port"
                                [label]="
                                    'sftpconnection_modal_port_label'
                                        | translate: 'online' : 'export'
                                "
                                [maxLength]="5" />
                        </div>
                    </div>

                    <app-input-text-group
                        formControlName="username"
                        [label]="
                            'sftpconnection_modal_username_label' | translate: 'online' : 'export'
                        "
                        [placeholder]="
                            'sftpconnection_modal_username_placeholder'
                                | translate: 'online' : 'export'
                        "
                        [maxLength]="usernameMaxLength" />

                    <div class="mb-3">
                        <div class="form-label required">
                            {{
                                'sftpconnection_modal_authentication_label'
                                    | translate: 'online' : 'export'
                            }}
                        </div>
                        <app-option-select
                            [options]="authenticationOptions"
                            [selectedId]="authenticationType()"
                            name="sftp-authentication-type"
                            ariaKey="sftpconnection_modal_authentication_label"
                            (optionSelected)="selectAuthenticationType($event)" />
                    </div>

                    @if (authenticationType() === 'SSH_KEY') {
                        <div class="mb-3">
                            <div class="form-label" [class.required]="isSecretRequired()">
                                {{
                                    'sftpconnection_modal_private_key_badge'
                                        | translate: 'online' : 'export'
                                }}
                            </div>
                            <app-input-file-drop-zone
                                [multiple]="false"
                                [title]="privateKeyTitle()"
                                [badge]="privateKeyBadge()"
                                [mutedBadge]="privateKeyState() === 'stored'"
                                (files)="pickPrivateKey($event)" />
                            @if (privateKeyError() === 'too-large') {
                                <p class="form-text text-danger mb-0">
                                    {{
                                        'sftpconnection_modal_private_key_too_large'
                                            | translate: 'online' : 'export'
                                    }}
                                </p>
                            } @else if (privateKeyError() === 'not-a-key') {
                                <p class="form-text text-danger mb-0">
                                    {{
                                        'sftpconnection_modal_private_key_not_a_key'
                                            | translate: 'online' : 'export'
                                    }}
                                </p>
                            } @else if (privateKeyState() === 'missing') {
                                <p class="form-text text-danger mb-0">
                                    {{
                                        'sftpconnection_modal_private_key_missing'
                                            | translate: 'online' : 'export'
                                    }}
                                </p>
                            }
                        </div>
                    } @else {
                        <app-input-text-group
                            formControlName="password"
                            type="password"
                            [required]="isSecretRequired()"
                            [label]="
                                'sftpconnection_modal_password_label'
                                    | translate: 'online' : 'export'
                            "
                            [placeholder]="
                                'sftpconnection_modal_password_placeholder'
                                    | translate: 'online' : 'export'
                            " />
                    }

                    <app-input-text-group
                        formControlName="baseFolder"
                        [label]="
                            'sftpconnection_modal_base_folder_label'
                                | translate: 'online' : 'export'
                        "
                        [placeholder]="
                            'sftpconnection_modal_base_folder_placeholder'
                                | translate: 'online' : 'export'
                        "
                        [information]="
                            'sftpconnection_modal_base_folder_hint' | translate: 'online' : 'export'
                        "
                        [maxLength]="baseFolderMaxLength" />

                    <app-input-text-group
                        formControlName="testPath"
                        [label]="
                            'sftpconnection_modal_test_path_label' | translate: 'online' : 'export'
                        "
                        [placeholder]="testPathPlaceholder()"
                        [information]="
                            'sftpconnection_modal_test_path_hint' | translate: 'online' : 'export'
                        "
                        [maxLength]="testPathMaxLength" />
                </div>
            </div>

            <div class="col-12 col-lg-6 d-flex flex-column gap-3">
                @if (usedByNote(); as note) {
                    <app-export-note icon="bi-exclamation-circle" tone="warning">
                        {{ note }}
                    </app-export-note>
                }
                <app-sftp-connection-test
                    class="flex-grow-1"
                    [progress]="testProgress()"
                    [running]="isTestRunning()"
                    [disabled]="isTestDisabled()"
                    (testRequested)="runTest()" />
                <app-export-note icon="bi-info-circle">
                    {{ 'sftpconnection_modal_credentials_note' | translate: 'online' : 'export' }}
                </app-export-note>
            </div>
        </div>
    </div>

    <div class="modal-footer">
        <button
            type="button"
            class="btn btn-outline-primary"
            [disabled]="isSaving()"
            (click)="dismiss()">
            {{ 'actions_cancel' | translate: 'online' : 'common' }}
        </button>
        <app-action-button
            type="submit"
            [label]="{key: submitKey, module: 'export'}"
            [busy]="isSaving()"
            [disabled]="isSaving()" />
    </div>
</form>
